import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  MoreVertical,
  Trash2,
  Play,
  Pause,
  Shield,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  RefreshCw,
} from 'lucide-react';
import { License, LicenseListResponse } from '../types/License';
import dashboardApi from '../api/dashboardApi';
import LicenseModal from './LicenseModal';
import { useNotification } from '../context/NotificationContext';
import './LicenseTable.css';

const formatExpiryDate = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const LicenseTable: React.FC = () => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const { success, error } = useNotification();

  const fetchLicenses = useCallback(async () => {
    try {
      setLoading(true);
      const data: LicenseListResponse = await dashboardApi.getLicenses({
        page: currentPage,
        limit: 20,
        status_filter: statusFilter || undefined,
        email_filter: searchEmail || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setLicenses(data.licenses);
      setTotalPages(data.pagination.total_pages);
      setTotalCount(data.pagination.total_count);
    } catch (err) {
      console.error('Error fetching licenses:', err);
      error('Failed to fetch licenses');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, searchEmail, sortBy, sortOrder, error]);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
  }, [statusFilter, searchEmail]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleAction = async (action: string, license: License) => {
    try {
      setActiveDropdown(null);
      switch (action) {
        case 'view':
          setLoadingDetails(true);
          try {
            const fullDetails = await dashboardApi.getLicenseDetails(license.id);
            setSelectedLicense(fullDetails);
            setShowModal(true);
          } catch (err) {
            console.error('Error fetching details:', err);
            error('Failed to fetch license details. Displaying partial data.');
            setSelectedLicense(license);
            setShowModal(true);
          } finally {
            setLoadingDetails(false);
          }
          break;
        case 'revoke':
          if (window.confirm('Revoke this license?')) {
            await dashboardApi.revokeLicense(license.id);
            success('License revoked');
            fetchLicenses();
          }
          break;
        case 'reactivate':
          if (window.confirm('Reactivate this license?')) {
            await dashboardApi.reactivateLicense(license.id);
            success('License reactivated');
            fetchLicenses();
          }
          break;
        case 'pause':
          if (window.confirm('Pause this license?')) {
            await dashboardApi.pauseLicense(license.id);
            success('License paused');
            fetchLicenses();
          }
          break;
        case 'delete':
          if (window.confirm('Delete this license permanently?')) {
            await dashboardApi.deleteLicense(license.id);
            success('License deleted');
            fetchLicenses();
          }
          break;
      }
    } catch (err) {
      console.error(`Failed to ${action} license:`, err);
      error(`Failed to ${action} license`);
    }
  };

  const expiryClass = (days: number) => {
    if (days <= 0) return 'bad';
    if (days <= 7) return 'bad';
    if (days <= 30) return 'warn';
    return 'ok';
  };

  return (
    <div className="lt-page">
      <div className="lt-toolbar">
        <div className="lt-toolbar-left">
          <div className="lt-search">
            <Search size={15} color="#94a3b8" />
            <input
              type="text"
              placeholder="Search org, email…"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
            />
          </div>
          <select
            className="lt-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="revoked">Revoked</option>
            <option value="paused">Paused</option>
          </select>
          <span className="lt-count">{totalCount} licenses</span>
        </div>
        <button type="button" className="lt-btn-refresh" onClick={fetchLicenses}>
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="lt-table-wrap">
        {loading && licenses.length > 0 && (
          <div className="lt-loading">
            <RefreshCw size={22} className="animate-spin" color="#64748b" />
          </div>
        )}
        <div className="lt-table-scroll">
          <table className="lt-table">
            <thead>
              <tr>
                <th>
                  <button type="button" className="lt-sort-btn" onClick={() => handleSort('org_name')}>
                    Organization <ArrowUpDown size={12} />
                  </button>
                </th>
                <th>Status</th>
                <th>Expiry</th>
                <th>IP</th>
                <th>Usage</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {licenses.length === 0 && !loading ? (
                <tr>
                  <td colSpan={6}>
                    <div className="lt-empty">No licenses match your filters.</div>
                  </td>
                </tr>
              ) : (
                licenses.map((license) => (
                  <tr key={license.id} onClick={() => handleAction('view', license)}>
                    <td title={`${license.org_name || '—'} · ${license.email}`}>
                      <span className="lt-org-name">{license.org_name?.trim() || '—'}</span>
                      <span className="lt-org-email">{license.email}</span>
                    </td>
                    <td>
                      <span className={`lt-status lt-status--${license.status}`}>
                        {license.status}
                      </span>
                    </td>
                    <td>
                      <span className="lt-expiry-date">{formatExpiryDate(license.expires_at)}</span>
                      <span className={`lt-expiry-left ${expiryClass(license.days_remaining)}`}>
                        {license.days_remaining > 0 ? `${license.days_remaining}d left` : 'Expired'}
                      </span>
                    </td>
                    <td>
                      <span className="lt-ip">{license.last_request_ip || '—'}</span>
                    </td>
                    <td>
                      <span className="lt-usage">
                        <strong>{license.request_count}</strong> req ·{' '}
                        <strong>{license.renewal_count || 0}</strong> ren
                      </span>
                    </td>
                    <td className="lt-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="lt-icon-btn"
                        title="More actions"
                        onClick={() => setActiveDropdown(activeDropdown === license.id ? null : license.id)}
                      >
                        <MoreVertical size={16} />
                      </button>
                      {activeDropdown === license.id && (
                        <div className="lt-dropdown">
                          {license.status === 'active' ? (
                            <>
                              <button type="button" onClick={() => handleAction('pause', license)}>
                                <Pause size={14} /> Pause
                              </button>
                              <button type="button" onClick={() => handleAction('revoke', license)}>
                                <Shield size={14} /> Revoke
                              </button>
                              <div className="lt-dropdown-divider" />
                              <button type="button" onClick={() => handleAction('delete', license)}>
                                <Trash2 size={14} /> Delete
                              </button>
                            </>
                          ) : (
                            <>
                              <button type="button" onClick={() => handleAction('reactivate', license)}>
                                <Play size={14} /> Reactivate
                              </button>
                              <button type="button" onClick={() => handleAction('delete', license)}>
                                <Trash2 size={14} /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="lt-pagination">
        <span>
          {(currentPage - 1) * 20 + 1}–{Math.min(currentPage * 20, totalCount)} of {totalCount}
        </span>
        <div className="lt-pagination-btns">
          <button
            type="button"
            className="lt-page-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <ChevronLeft size={16} /> Prev
          </button>
          {Array.from({ length: totalPages }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`lt-page-btn${currentPage === i + 1 ? ' active' : ''}`}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            type="button"
            className="lt-page-btn"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {loadingDetails && (
        <div className="lt-loading-overlay">
          <div className="lt-loading-box">
            <RefreshCw size={20} className="animate-spin" />
            Loading…
          </div>
        </div>
      )}

      {showModal && selectedLicense && (
        <LicenseModal
          license={selectedLicense}
          onClose={() => {
            setShowModal(false);
            setSelectedLicense(null);
          }}
        />
      )}
    </div>
  );
};

export default LicenseTable;
